import { Modal } from "@/components/elements/Modal/Modal";
import { text } from "@/theme/colors";
import { FCC } from "@/types/common";
import { isApiError } from "@/utils/error";
import { truncateWithEllipsis } from "@/utils/string";
import { Box, Stack, Typography } from "@mui/material";

export type TCannotDeactivateUser = {
  fullName: string;
  email: string;
};

// Returns the offenders if `error` is a PIC-guard error, else null (caller falls back to a toast).
// The offender list rides in `error` (via the exception `cause`); the guard is identified by that
// shape alone, so any 停止/削除 block carrying an array of offenders is handled here.
export const getPICGuardOffenders = (error: unknown): TCannotDeactivateUser[] | null => {
  if (!isApiError(error) || !Array.isArray(error.error)) return null;
  return (error.error as unknown as TCannotDeactivateUser[]).map((offender) => ({
    fullName: offender.fullName ?? "",
    email: offender.email ?? "",
  }));
};

type TUserCannotDeactivateModalProps = {
  isOpen: boolean;
  onClose: () => void;
  users: TCannotDeactivateUser[];
  selectedCount?: number;
  errorMessage: string;
};

// Shown when 停止/削除 is blocked because the selected users are set as an organization's 担当者.
// Single target → plain message; multiple → header + a "Name (email)" bulleted list.
export const UserCannotDeactivateModal: FCC<TUserCannotDeactivateModalProps> = ({ isOpen, onClose, users, selectedCount, errorMessage }) => {
  const isSingle = (selectedCount ?? users.length) <= 1;

  return (
    <Modal isOpen={isOpen} onClose={onClose} labelClose="閉じる" width={500} enableFooter hiddenOkBtn justifyBtnAction="center">
      <Box mb={2}>
        <Typography variant="body1" sx={{ mb: isSingle ? 1 : 2, mt: 1 }}>
          {errorMessage}
        </Typography>
        {!isSingle && (
          <Stack spacing={1.5} sx={{ maxHeight: "500px", overflow: "auto" }}>
            {users.map((user, index) => {
              const label = `${user.fullName} (${user.email})`;
              return (
                <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography component="span" sx={{ color: text.primary, lineHeight: 1, flexShrink: 0 }}>
                    ・
                  </Typography>
                  <Typography variant="body1" title={label}>
                    {truncateWithEllipsis(label, 40)}
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>
    </Modal>
  );
};
